import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = '/api/v1/public/fsm';

const PublicEquipmentPage = () => {
  const { qrCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    // Detect Arabic browser
    const browserLang = navigator.language || '';
    if (browserLang.startsWith('ar')) setLang('ar');
    fetchData();
  }, [qrCode]);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/equipment/${qrCode}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Not found');
      setData(json.data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const isAr = lang === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(isAr ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusColors = {
    active: { bg: '#065f46', text: '#d1fae5', label: isAr ? 'يعمل' : 'Active' },
    inactive: { bg: '#7c2d12', text: '#fed7aa', label: isAr ? 'متوقف' : 'Inactive' },
    maintenance: { bg: '#92400e', text: '#fef3c7', label: isAr ? 'قيد الصيانة' : 'Under Maintenance' },
    retired: { bg: '#4a044e', text: '#f5d0fe', label: isAr ? 'متقاعد' : 'Retired' },
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: 'system-ui, Cairo, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16, animation: 'spin 1s linear infinite' }}>⚙️</div>
          <p style={{ color: '#6b7280', fontSize: 16 }}>{isAr ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: 'system-ui, Cairo, sans-serif' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <h2 style={{ color: '#1f2937', marginBottom: 8 }}>{isAr ? 'لم يتم العثور على المعدة' : 'Equipment Not Found'}</h2>
          <p style={{ color: '#6b7280' }}>{isAr ? 'رمز QR غير صالح أو المعدة غير متاحة' : 'Invalid QR code or equipment not available'}</p>
          <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 12 }}>QR: {qrCode}</p>
        </div>
      </div>
    );
  }

  const s = statusColors[data.status] || statusColors.active;
  const hasContract = data.has_active_contract;

  return (
    <div dir={dir} style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', Cairo, system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1a2038 100%)', color: 'white', padding: '24px 20px 32px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          {/* Lang Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>{data.organization}</span>
            <button onClick={() => setLang(isAr ? 'en' : 'ar')} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
              padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            }}>
              {isAr ? 'English' : 'عربي'}
            </button>
          </div>

          {/* Equipment Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 36 }}>{data.category_icon || '⚙️'}</span>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{isAr ? (data.name_ar || data.name) : data.name}</h1>
              <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: 14 }}>
                {isAr ? (data.category_ar || data.category) : data.category}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div style={{ marginTop: 16 }}>
            <span style={{
              display: 'inline-block', padding: '4px 14px', borderRadius: 20,
              fontSize: 13, fontWeight: 600, backgroundColor: s.bg, color: s.text,
            }}>
              {s.label}
            </span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '-16px auto 0', padding: '0 16px 32px' }}>

        {/* Contract Coverage Card */}
        <div style={{
          background: 'white', borderRadius: 16, padding: 20, marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: hasContract ? '2px solid #059669' : '2px solid #dc2626',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 22 }}>{hasContract ? '✅' : '⚠️'}</span>
            <h2 style={{ margin: 0, fontSize: 16, color: '#1f2937' }}>
              {hasContract
                ? (isAr ? 'مغطى بعقد صيانة' : 'Covered by Service Contract')
                : (isAr ? 'غير مشمول بعقد صيانة' : 'No Active Service Contract')}
            </h2>
          </div>

          {hasContract ? data.contracts.map((c, i) => (
            <div key={i} style={{
              background: '#f0fdf4', borderRadius: 12, padding: 14, marginBottom: i < data.contracts.length - 1 ? 10 : 0,
              border: '1px solid #bbf7d0',
            }}>
              <div style={{ fontWeight: 600, color: '#065f46', fontSize: 14, marginBottom: 8 }}>
                {isAr ? (c.title_ar || c.title) : c.title}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13, color: '#374151' }}>
                <div><span style={{ color: '#6b7280' }}>{isAr ? 'رقم العقد' : 'Contract'}:</span> {c.contract_number}</div>
                <div><span style={{ color: '#6b7280' }}>{isAr ? 'التغطية' : 'Coverage'}:</span> {isAr ? c.coverage_ar : c.coverage}</div>
                <div><span style={{ color: '#6b7280' }}>{isAr ? 'ينتهي' : 'Expires'}:</span> {formatDate(c.end_date)}</div>
                <div><span style={{ color: '#6b7280' }}>{isAr ? 'الاستجابة' : 'Response'}:</span> {c.sla_response_hours ? c.sla_response_hours + (isAr ? ' ساعات' : 'h') : '—'}</div>
                {c.visits_remaining !== null && (
                  <div><span style={{ color: '#6b7280' }}>{isAr ? 'زيارات متبقية' : 'Visits Left'}:</span> {c.visits_remaining}</div>
                )}
              </div>
            </div>
          )) : (
            <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0' }}>
              {isAr ? 'يرجى التواصل مع مزود الخدمة لتفعيل عقد صيانة.' : 'Please contact the service provider to activate a maintenance contract.'}
            </p>
          )}
        </div>

        {/* Equipment Details Card */}
        <div style={{
          background: 'white', borderRadius: 16, padding: 20, marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <h2 style={{ margin: '0 0 14px', fontSize: 16, color: '#1f2937' }}>
            {isAr ? 'تفاصيل المعدة' : 'Equipment Details'}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', fontSize: 13 }}>
            {[
              [isAr ? 'الماركة' : 'Brand', data.brand],
              [isAr ? 'الموديل' : 'Model', data.model],
              [isAr ? 'الرقم التسلسلي' : 'Serial', data.serial_number],
              [isAr ? 'تاريخ التركيب' : 'Installed', formatDate(data.installation_date)],
              [isAr ? 'الطابق' : 'Floor', data.floor],
              [isAr ? 'الوحدة' : 'Unit', data.unit_number],
              [isAr ? 'الموقع' : 'Location', isAr ? (data.location_ar || data.location) : data.location],
            ].filter(([, v]) => v && v !== '—').map(([label, value], i) => (
              <div key={i}>
                <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>{label}</div>
                <div style={{ color: '#1f2937', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Warranty */}
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 10,
            background: data.warranty_active ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${data.warranty_active ? '#bbf7d0' : '#fecaca'}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>{data.warranty_active ? '🛡️' : '⏰'}</span>
            <div style={{ fontSize: 13 }}>
              <strong style={{ color: data.warranty_active ? '#065f46' : '#991b1b' }}>
                {data.warranty_active
                  ? (isAr ? 'الضمان ساري' : 'Warranty Active')
                  : (isAr ? 'الضمان منتهي' : 'Warranty Expired')}
              </strong>
              {data.warranty_expiry && (
                <span style={{ color: '#6b7280', marginInlineStart: 8 }}>
                  ({isAr ? 'حتى' : 'until'} {formatDate(data.warranty_expiry)})
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Report Fault Button */}
        {data.report_fault_link && (
          <a href={data.report_fault_link} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '16px 20px',
            background: '#25d366', color: 'white', textDecoration: 'none',
            borderRadius: 16, fontSize: 16, fontWeight: 700,
            boxShadow: '0 4px 12px rgba(37,211,102,0.4)',
            transition: 'transform 0.1s',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {isAr ? 'الإبلاغ عن عطل عبر واتساب' : 'Report Fault via WhatsApp'}
          </a>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 24, color: '#9ca3af', fontSize: 11 }}>
          <p>QR: {data.qr_code}</p>
          <p>{isAr ? 'مدعوم بواسطة' : 'Powered by'} <strong>MatixFSM</strong></p>
        </div>
      </div>
    </div>
  );
};

export default PublicEquipmentPage;
